import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import HoldComposer, {
  HOLD_COMPOSER_DEFAULTS,
  NEW_SELECTION_DEFAULTS,
  PORTION_OPTIONS,
  isSelectionComplete,
  portionSlots,
  resolveHoldDuration,
  resolveHoldNotifications,
} from '../components/holds/HoldComposer';
import HoldCard from '../components/holds/HoldCard';
import HoldDatePicker from '../components/holds/HoldDatePicker';
import { HOLD_TYPE_OPTIONS, buildHoldCards, formatShortDate } from '../components/holds/holdUtils';

const defaultInquiry = {
  inquiryNumber: 'INQ-001',
  status: 'Converted',
  mediator: 'Eric Anderson',
  requestingParty: 'Littler - Sarah Chen',
  email: 'sarah@littler.com',
  dateSubmitted: 'March 10, 2026',
  requestedDates: 'April 15-17, 2026',
  notes: 'Client prefers morning sessions. Has scheduling conflict on April 16 afternoon.',
};

const defaultTasks = [
  {
    id: 1,
    task: 'Send confirmation email to requesting party',
    dueDate: 'Apr 20',
    assignee: 'NK',
    avatarBg: '#115e59',
    completed: true,
  },
  {
    id: 2,
    task: 'Confirm mediator availability for requested dates',
    dueDate: 'Apr 18',
    assignee: 'RC',
    avatarBg: '#8b5cf6',
    completed: true,
  },
  {
    id: 3,
    task: 'Follow up with requesting party in 1 week',
    dueDate: 'Apr 25',
    assignee: 'NK',
    avatarBg: '#115e59',
    completed: false,
  },
  {
    id: 4,
    task: 'Prepare mediation agreement draft',
    dueDate: 'May 1',
    assignee: 'RC',
    avatarBg: '#8b5cf6',
    completed: false,
  },
];

const defaultComments = [
  {
    id: 1,
    initials: 'NK',
    name: 'Nikki Safavi',
    time: 'Mar 11, 2026 at 9:15 AM',
    text: 'Contacted Sarah Chen to confirm the requested dates. She mentioned they may need to adjust to April 16-18 instead.',
  },
  {
    id: 2,
    initials: 'RC',
    name: 'Rocio Ceron',
    time: 'Mar 12, 2026 at 2:30 PM',
    text: 'Mediator confirmed availability for April 15-17. Sending updated confirmation to all parties.',
  },
];

function BackArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 14.25L3.75 9L9 3.75" stroke="#5b5a64" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.75 9H14.25" stroke="#5b5a64" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.917 7H11.083" stroke="#ffffff" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 2.917V11.083" stroke="#ffffff" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 5.5L5 8.5L10 3" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="#5b5a64" strokeWidth="1.167" />
    </svg>
  );
}

function EnvelopeIcon({ color = '#115e59' }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <rect x="1.25" y="2.5" width="9.5" height="7" rx="1.25" stroke={color} strokeWidth="1" />
      <path d="M1.5 3.25L6 6.25L10.5 3.25" stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ExternalLinkIcon({ color = '#115e59' }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <path d="M5 2H2.5C2.22 2 2 2.22 2 2.5V9.5C2 9.78 2.22 10 2.5 10H9.5C9.78 10 10 9.78 10 9.5V7" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 2H10V5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 6.5L10 2" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon({ color = '#6b7280' }) {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="3" width="10" height="9" rx="1.5" stroke={color} strokeWidth="1.1" />
      <path d="M2 5.5H12" stroke={color} strokeWidth="1.1" />
      <path d="M5 2V4" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
      <path d="M9 2V4" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
}

function CopyIcon({ color = '#6b7280', size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <rect x="4" y="4" width="8" height="8" rx="1.5" stroke={color} strokeWidth="1.1" />
      <path d="M10 4V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h1" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
}

function CheckMarkIcon({ color = '#115e59', size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7L5.5 10L11.5 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UserIcon({ color = '#6b7280', size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="5" r="2.25" stroke={color} strokeWidth="1.1" />
      <path d="M2.75 11.5C3.4 9.7 5.1 8.5 7 8.5C8.9 8.5 10.6 9.7 11.25 11.5" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
}

function ChevronTinyIcon({ color = '#115e59', size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none">
      <path d="M2.5 3.75L5 6.25L7.5 3.75" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Platform-aware modifier symbol for the hold modal's keyboard hint. */
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const CMD_SYMBOL = IS_MAC ? '⌘' : 'Ctrl';

/* Small <kbd>-style key chips for shortcut hints (e.g. ⌘ ↵). */
function KbdKeys({ keys }) {
  return (
    <span className="inline-flex items-center" style={{ gap: 2 }}>
      {keys.map((k, i) => (
        <kbd
          key={i}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 10.5,
            fontWeight: 600,
            color: '#6b7280',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            padding: '1px 5px',
            lineHeight: 1.5,
          }}
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}

/* The hold surface's calendar glyph — the "Hold Dates" button and nothing
   else, so it can carry the amber hold colour without touching the teal
   CalendarIcon the meta strip uses. */
function HoldCalendarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="12" height="11" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 6.4h12M5.6 1.8v2M10.4 1.8v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

const STATUS_STYLE_MAP = {
  'In Progress': { text: '#92400e', bg: '#fef3c7', dot: '#d97706', ring: '#fde68a' },
  'New':         { text: '#1d4ed8', bg: '#e8f4fd', dot: '#3b82f6', ring: '#bfdbfe' },
  'Converted':   { text: '#115e59', bg: '#e8fdf5', dot: '#10b981', ring: '#a7f3d0' },
  'Not Converted': { text: '#b91c1c', bg: '#fde8e9', dot: '#dc2626', ring: '#fecaca' },
};

// Status dropdown options.  "New" is the default for any inquiry that
// hasn't been touched yet, but we don't want users to manually mark
// something back to New — only forward states are selectable.
const STATUS_OPTIONS_ALL = ['In Progress', 'Converted', 'Not Converted'];

// Shared inline-style objects for the summary card's meta strip.
// Keeping them at file scope ensures the three fragments (Mediator,
// Requested, Submitted) render with byte-identical typography so labels
// share a baseline.
const metaLabelStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 10.5,
  fontWeight: 500,
  color: '#9ca3af',
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};
const metaValueStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
};

function getStatusStyle(status) {
  return STATUS_STYLE_MAP[status] || { text: '#5b5a64', bg: '#f3f4f6', dot: '#9ca3af', ring: '#e5e7eb' };
}

function getInitials(name) {
  if (!name) return '';
  return name.split(/\s+/).map(s => s[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
}

// submission_date / follow_up_date are plain DATE columns ("YYYY-MM-DD");
// created_at is a full ISO timestamp. Reduce both to the bare date part —
// that's what a <input type="date"> round-trips, and parsing a bare date
// with `new Date()` treats it as UTC midnight and renders the previous day
// west of Greenwich (the mediation_date off-by-one we fixed elsewhere).
function toDatePart(value) {
  if (!value) return '';
  const m = String(value).match(/^\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : '';
}

// created_at is a full timestamp, not a bare DATE. Reduce it to the Pacific
// business-day date (YYYY-MM-DD) — the same timezone the Inquiries list renders
// created_at in — so the submission-date fallback for pre-migration rows doesn't
// jump a day ahead for evening-Pacific inquiries (and isn't persisted wrong on
// Save). `en-CA` formats as YYYY-MM-DD.
function isoToPacificDatePart(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
}

// Render a bare YYYY-MM-DD as a long date WITHOUT going through UTC — build
// the Date from local Y/M/D parts so it never drifts a day.
function formatLongDate(value) {
  const iso = toDatePart(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const CASE_TYPE_OPTIONS = ['Employment', 'Commercial', 'Personal Injury', 'Family', 'Real Estate', 'Construction', 'Other'];

// Edit-inquiry modal. Mirrors the "Log New Inquiry" modal styling on the
// Inquiries list page (inline inputStyle / labelStyle). Edits the inquiry's
// own contact + case fields plus the two operator-facing dates, all of which
// CM-be allowlists on PUT /inquiries/:id.
function EditInquiryModal({ id, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    requestingParty: initial.requestingParty || '',
    email: initial.email || '',
    phone: initial.phone || '',
    firm: initial.firm || '',
    caseName: initial.caseName || '',
    caseType: initial.caseType || '',
    submissionDate: initial.submissionDate || '',
    followUpDate: initial.followUpDate || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    const name = form.requestingParty.trim();
    const email = form.email.trim();
    if (!name || !email) {
      setError('Name and Email are required');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.put(`/inquiries/${id}`, {
        requesting_party: name,
        contact_email: email,
        contact_phone: form.phone.trim(),
        firm: form.firm.trim(),
        case_name: form.caseName.trim(),
        case_type: form.caseType || null,
        // Bare YYYY-MM-DD (or null) — plain DATE columns; never send a full ISO.
        submission_date: form.submissionDate || null,
        follow_up_date: form.followUpDate || null,
      });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update inquiry');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    height: 42,
    borderRadius: 8,
    border: '1px solid #e2e2e2',
    backgroundColor: '#ffffff',
    padding: '0 14px',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    color: '#0d0d0d',
    outline: 'none',
  };
  const labelStyle = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    fontWeight: 500,
    color: '#0d0d0d',
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 1000, backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      onClick={onClose}
    >
      <div
        className="flex flex-col"
        style={{ width: 640, maxHeight: '90vh', borderRadius: 16, backgroundColor: '#fafbfc', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative shrink-0" style={{ height: 92 }}>
          <div style={{ position: 'absolute', left: 32, top: 26, width: 4, height: 40, borderRadius: 2, backgroundColor: '#115e59' }} />
          <div className="flex flex-col" style={{ position: 'absolute', left: 52, top: 26, gap: 4 }}>
            <span style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 20, fontWeight: 700, color: '#0d0d0d', lineHeight: '24px' }}>
              Edit Inquiry Details
            </span>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 400, color: '#94949a', lineHeight: '17px' }}>
              Update the contact, firm, case and date details
            </span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center"
            style={{ position: 'absolute', right: 21, top: 27, width: 36, height: 36, borderRadius: 8, backgroundColor: '#f5f5f5', border: 'none', cursor: 'pointer' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#999999" strokeWidth="1.667" strokeLinecap="round" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col overflow-y-auto" style={{ padding: '20px 36px 28px 36px', gap: 16 }}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#dc2626' }}>{error}</span>
            </div>
          )}

          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={labelStyle}>Requesting Firm</span>
            <input type="text" placeholder="e.g. Littler Mendelson" value={form.firm} onChange={set('firm')} style={inputStyle} />
          </div>

          <div className="flex" style={{ gap: 14 }}>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Name <span style={{ color: '#dc2626' }}>*</span></span>
              <input type="text" placeholder="Full name" value={form.requestingParty} onChange={set('requestingParty')} style={inputStyle} />
            </div>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Email <span style={{ color: '#dc2626' }}>*</span></span>
              <input type="email" placeholder="email@example.com" value={form.email} onChange={set('email')} style={inputStyle} />
            </div>
          </div>

          <div className="flex" style={{ gap: 14 }}>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Phone</span>
              <input type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={set('phone')} style={inputStyle} />
            </div>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Case Name</span>
              <input type="text" placeholder="Enter case name" value={form.caseName} onChange={set('caseName')} style={inputStyle} />
            </div>
          </div>

          <div className="flex" style={{ gap: 14 }}>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Case Type</span>
              <div style={{ position: 'relative' }}>
                <select
                  value={form.caseType}
                  onChange={set('caseType')}
                  style={{ ...inputStyle, appearance: 'none', color: form.caseType ? '#0d0d0d' : '#bcbcbc' }}
                >
                  <option value="">Select type...</option>
                  {CASE_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <ChevronDownIcon />
                </div>
              </div>
            </div>
            <div className="flex-1" />
          </div>

          <div className="flex" style={{ gap: 14 }}>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Submission Date</span>
              <input type="date" value={form.submissionDate} onChange={set('submissionDate')} style={{ ...inputStyle, color: form.submissionDate ? '#0d0d0d' : '#9ca3af' }} />
            </div>
            <div className="flex flex-col flex-1" style={{ gap: 6 }}>
              <span style={labelStyle}>Follow-Up Date</span>
              <input type="date" value={form.followUpDate} onChange={set('followUpDate')} style={{ ...inputStyle, color: form.followUpDate ? '#0d0d0d' : '#9ca3af' }} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end shrink-0" style={{ padding: '18px 32px 26px 32px', gap: 12 }}>
          <button
            onClick={onClose}
            style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #e2e2e2', backgroundColor: 'transparent', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 500, color: '#888888' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '10px 24px', borderRadius: 8, backgroundColor: saving ? '#5b9e9a' : '#115e59', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#ffffff' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Delete confirmation modal — the delete is a soft delete on the backend
// (deleted_at), but a destructive action still gets an explicit confirm.
function DeleteInquiryModal({ inquiryNumber, deleting, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 1000, backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      onClick={onCancel}
    >
      <div
        className="flex flex-col bg-white"
        style={{ width: 420, borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '28px 28px 8px 28px' }}>
          <h3 style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            Delete inquiry?
          </h3>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13.5, color: '#4b5563', lineHeight: 1.6, margin: 0 }}>
            {inquiryNumber ? `${inquiryNumber} ` : 'This inquiry '}
            will be removed from your inquiries list. This action can only be reversed by an administrator.
          </p>
        </div>
        <div className="flex items-center justify-end" style={{ padding: '20px 28px 26px 28px', gap: 12 }}>
          <button
            onClick={onCancel}
            style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #e2e2e2', backgroundColor: 'transparent', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 500, color: '#4b5563' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{ padding: '10px 20px', borderRadius: 8, backgroundColor: deleting ? '#f0a3a3' : '#dc2626', border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#ffffff' }}
          >
            {deleting ? 'Deleting...' : 'Delete inquiry'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InquiryDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [inquiry, setInquiry] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', dueDate: '', assignee: '' });
  const [addingTask, setAddingTask] = useState(false);
  const [casesList, setCasesList] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [linkingCase, setLinkingCase] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseDropdownOpen, setCaseDropdownOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [copiedField, setCopiedField] = useState(''); // 'email' | 'phone' | ''
  const [toast, setToast] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* ---------------- Hold dates ----------------
     Ported from the Scale mediator portal. Holds are chosen by DATE and
     half-day, never by availability id: the backend resolves a (date, slot)
     pair to the row itself.

     One entry per picked date, IN PICK ORDER:
       { date, portion, preset, customAmount, customUnit }
     Booking slot and duration are PER DATE — a firm asks for the 25th all day
     or the 22nd afternoon, and one batch choice forced every date down to
     whatever the most awkward one could take. Hold type and the two
     notification switches stay BATCH: they describe the request, not a date. */
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdDates, setHoldDates] = useState([]);
  // The date currently being configured. Picking a date makes it active.
  const [activeHoldDate, setActiveHoldDate] = useState('');
  // 'config' while setting dates, 'review' for the summary before saving.
  const [holdScreen, setHoldScreen] = useState('config');
  const [holdType, setHoldType] = useState('soft');
  const [holdOptions, setHoldOptions] = useState(HOLD_COMPOSER_DEFAULTS);
  const [savingHold, setSavingHold] = useState(false);
  const [holdError, setHoldError] = useState('');
  const [releasingDate, setReleasingDate] = useState('');
  /* The hold awaiting a release decision: { date, slot }. Null when the dialog
     is closed. Held separately from releasingDate so the card can show its
     spinner while the request is in flight and the dialog is already gone. */
  const [pendingRelease, setPendingRelease] = useState(null);
  // Keyed by date+slot so releasing one half does not grey out the other.
  const holdKey = (date, slot) => `${date}|${slot || 'all'}`;
  const [holdCalendar, setHoldCalendar] = useState(null);
  /* True while a month's availability is in flight. Needed because a null
     holdCalendar means "assume everything is free" — deliberate, so a slow
     request cannot make a whole month look unavailable, but it also means the
     calendar paints every day as selectable until the fetch lands and then
     repaints. The skeleton covers exactly that gap. */
  const [loadingHoldCalendar, setLoadingHoldCalendar] = useState(false);
  const holdRangeRef = useRef(null);

  useEffect(() => {
    if (!caseDropdownOpen) return;
    const close = (e) => {
      if (!e.target.closest('[data-case-dropdown]')) setCaseDropdownOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [caseDropdownOpen]);

  useEffect(() => {
    if (!statusMenuOpen) return;
    const close = (e) => {
      if (!e.target.closest('[data-status-menu]')) setStatusMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [statusMenuOpen]);

  useEffect(() => {
    if (!copiedField) return;
    const t = setTimeout(() => setCopiedField(''), 1800);
    return () => clearTimeout(t);
  }, [copiedField]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  /* Lock the page behind the hold modals.
     Both are portalled to document.body and sit on a fixed overlay, so the
     page underneath never stopped scrolling: reaching the end of the calendar
     and carrying on scrolled the inquiry page instead, and the modal appeared
     to slide away. The overlay covers the page, it does not freeze it.

     The padding compensation matters as much as the overflow — removing the
     scrollbar reclaims its width, and without a matching pad every fixed and
     full-width element jumps sideways as the modal opens. On macOS with
     overlay scrollbars the difference is 0 and this is a no-op. Restores the
     previous inline values rather than blanking them, so it cannot clobber a
     lock some other component set. */
  const holdModalOpen = showHoldModal || !!pendingRelease;
  useEffect(() => {
    if (!holdModalOpen) return undefined;
    const { overflow, paddingRight } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [holdModalOpen]);

  const handleCopyValue = async (value, field) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
    } catch (err) {
      // Fallback: ignore — non-secure context. We still flag the copy state for visual feedback.
      setCopiedField(field);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (!inquiry || nextStatus === inquiry.status || savingStatus) {
      setStatusMenuOpen(false);
      return;
    }
    const prev = inquiry.status;
    setInquiry((cur) => (cur ? { ...cur, status: nextStatus } : cur));
    setStatusMenuOpen(false);
    setSavingStatus(true);
    try {
      await api.put(`/inquiries/${id}`, { status: nextStatus });
      setToast(`Status changed to ${nextStatus}`);
    } catch (err) {
      console.error('Failed to update status:', err);
      setInquiry((cur) => (cur ? { ...cur, status: prev } : cur));
      setToast('Could not update status');
    } finally {
      setSavingStatus(false);
    }
  };

  useEffect(() => {
    api.get('/cases?page=1&limit=200')
      .then(res => setCasesList((res.data?.data || []).map(c => ({ id: c.id, name: c.caseName || c.caseNumber || `Case ${c.id}` }))))
      .catch(() => {});
  }, []);

  const handleLinkCase = async () => {
    if (!selectedCaseId) return;
    setLinkingCase(true);
    try {
      // Store linked case ID in notes JSON and update status
      const selectedCase = casesList.find(c => String(c.id) === String(selectedCaseId));
      await api.post(`/inquiries/${id}/comments`, {
        author: 'System',
        text: `Linked to Case #${selectedCaseId}${selectedCase ? ' — ' + selectedCase.name : ''}`,
      });
      await api.put(`/inquiries/${id}`, { status: 'Converted', converted_case_id: selectedCaseId });
      fetchInquiry();
    } catch (err) {
      console.error('Failed to link case:', err);
    } finally {
      setLinkingCase(false);
    }
  };

  // Unlink the case from this inquiry: clear converted_case_id (NULL) and move
  // the status back to "Not Converted" so the two stay consistent.
  const handleUnlinkCase = async () => {
    if (linkingCase) return;
    setLinkingCase(true);
    try {
      await api.put(`/inquiries/${id}`, { status: 'Not Converted', converted_case_id: null });
      fetchInquiry();
    } catch (err) {
      console.error('Failed to unlink case:', err);
    } finally {
      setLinkingCase(false);
    }
  };

  const fetchInquiry = useCallback(() => {
    api.get(`/inquiries/${id}`)
      .then(res => {
        const d = res.data.data;
        if (d) {
          setInquiry({
            inquiryNumber: d.inquiry_number || '',
            status: d.status || '',
            mediator: d.mediator_name || '',
            requestingParty: d.requesting_party ? `${d.firm || ''} - ${d.requesting_party}` : '',
            email: d.contact_email || '',
            // Raw editable fields kept separate from the composed display
            // strings above, so the Edit modal round-trips clean values.
            firm: d.firm || '',
            requestingPartyName: d.requesting_party || '',
            phone: d.contact_phone || '',
            caseName: d.case_name || '',
            caseType: d.case_type || '',
            // Submission Date = the operator-facing date column. For pre-migration
            // rows (null submission_date) fall back to created_at reduced to the
            // Pacific business day, matching the list — NOT the raw UTC slice,
            // which drifts a day for evening-Pacific rows and would be persisted
            // on Save. Both end up bare DATE values, formatted WITHOUT UTC.
            submissionDate: d.submission_date
              ? toDatePart(d.submission_date)
              : isoToPacificDatePart(d.created_at),
            submissionDateLabel: formatLongDate(
              d.submission_date
                ? toDatePart(d.submission_date)
                : isoToPacificDatePart(d.created_at)
            ),
            followUpDate: toDatePart(d.follow_up_date),
            followUpDateLabel: formatLongDate(d.follow_up_date),
            requestedDates: d.requested_dates || '',
            notes: d.notesText || (typeof d.notes === 'string' && d.notes.startsWith('{') ? '' : d.notes) || '',
            convertedCaseId: d.converted_case_id || null,
            // One entry per held DATE, already collapsed and sorted by the
            // backend, each carrying its held halves in `slots`. Defaulted to
            // an empty array so every consumer can read `.length` on an
            // inquiry the API answered before holds existed.
            holds: Array.isArray(d.holds) ? d.holds : [],
          });
          setTasks(Array.isArray(d.tasks) ? d.tasks : []);
          setComments(Array.isArray(d.comments) ? d.comments : []);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleDeleteInquiry = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/inquiries/${id}`);
      navigate('/inquiries');
    } catch (err) {
      console.error('Failed to delete inquiry:', err);
      setDeleting(false);
      setShowDeleteModal(false);
      setToast('Could not delete inquiry');
    }
  };

  const handlePostComment = async () => {
    if (!commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      const authorName = user?.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}` : 'Admin';
      const res = await api.post(`/inquiries/${id}/comments`, { author: authorName, text: commentText.trim() });
      if (res.data?.data) {
        setComments(res.data.data);
      } else {
        fetchInquiry();
      }
      setCommentText('');
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleToggleTask = async (taskId) => {
    const updatedTasks = tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t);
    setTasks(updatedTasks);
    try {
      await api.put(`/inquiries/${id}/tasks`, { tasks: updatedTasks });
    } catch (err) {
      console.error('Failed to update tasks:', err);
      fetchInquiry();
    }
  };

  const handleAddTask = async () => {
    if (!newTask.task.trim() || addingTask) return;
    setAddingTask(true);
    const taskToAdd = {
      id: Date.now(),
      task: newTask.task,
      dueDate: newTask.dueDate,
      assignee: newTask.assignee || 'UN',
      avatarBg: '#115e59',
      completed: false,
    };
    const updatedTasks = [...tasks, taskToAdd];
    try {
      await api.put(`/inquiries/${id}/tasks`, { tasks: updatedTasks });
      setTasks(updatedTasks);
      setNewTask({ task: '', dueDate: '', assignee: '' });
      setShowAddTask(false);
    } catch (err) {
      console.error('Failed to add task:', err);
    } finally {
      setAddingTask(false);
    }
  };

  useEffect(() => {
    fetchInquiry();
  }, [id, fetchInquiry]);

  /* ==================== Hold dates ==================== */

  const handleCloseHold = () => {
    if (savingHold) return;
    setShowHoldModal(false);
    setHoldDates([]);
    setActiveHoldDate('');
    setHoldScreen('config');
    setHoldType('soft');
    setHoldOptions(HOLD_COMPOSER_DEFAULTS);
    setHoldError('');
    setHoldCalendar(null);
    holdRangeRef.current = null;
  };

  /* Which days are already booked or held, for the month on screen. The picker
     calls this as the user pages through months; the range is remembered so a
     re-render does not refetch the same window, and so the same window can be
     refreshed after a hold is placed or released. */
  const loadHoldCalendar = useCallback(
    async (from, to) => {
      holdRangeRef.current = { from, to };
      setLoadingHoldCalendar(true);
      try {
        const res = await api.get(`/inquiries/${id}/hold-calendar?from=${from}&to=${to}`);
        // Ignore a response that arrives after the user has paged on.
        if (holdRangeRef.current?.from !== from || holdRangeRef.current?.to !== to) return;
        setHoldCalendar(res.data?.data || {});
      } catch (err) {
        console.error('Failed to load hold calendar:', err);
        // Leave it null — the picker stays fully selectable and the server
        // still refuses anything invalid, so the worst case is the old
        // behaviour rather than a dead calendar.
        setHoldCalendar(null);
      } finally {
        // Only the newest request may clear the flag, for the same reason its
        // response is the only one allowed to land: a slow reply for a month
        // already paged away from would switch the loader off over the wrong
        // data.
        if (holdRangeRef.current?.from === from && holdRangeRef.current?.to === to) {
          setLoadingHoldCalendar(false);
        }
      }
    },
    [id]
  );

  const refreshHoldCalendar = () => {
    const r = holdRangeRef.current;
    if (r) loadHoldCalendar(r.from, r.to);
  };

  /* The date being configured. */
  const activeSelection = holdDates.find((s) => s.date === activeHoldDate) || null;

  /* Picking a date makes it the one you are setting — clicking a day and
     having the panel still edit a different one is the confusion this avoids. */
  const pickHoldDate = (iso) => {
    setHoldDates((prev) =>
      prev.some((s) => s.date === iso)
        ? prev
        : [...prev, { date: iso, portion: null, ...NEW_SELECTION_DEFAULTS, preset: null }]
    );
    setActiveHoldDate(iso);
  };

  const removeHoldDate = (iso) => {
    setHoldDates((prev) => {
      const next = prev.filter((s) => s.date !== iso);
      if (iso === activeHoldDate) {
        const fallback = next.find((s) => !isSelectionComplete(s)) || next[0];
        setActiveHoldDate(fallback ? fallback.date : '');
      }
      if (next.length === 0) setHoldScreen('config');
      return next;
    });
  };

  /* Merge a change into the active date only, and move on once it is finished.
     Auto-advance replaces pager arrows: selecting is what moves you. */
  const patchActiveSelection = (patch) => {
    setHoldDates((prev) => {
      const next = prev.map((s) => (s.date === activeHoldDate ? { ...s, ...patch } : s));
      const me = next.find((s) => s.date === activeHoldDate);
      if (isSelectionComplete(me)) {
        const waiting = next.find((s) => !isSelectionComplete(s));
        if (waiting) setTimeout(() => setActiveHoldDate(waiting.date), 550);
      }
      return next;
    });
  };

  /* A save is all-or-nothing: if any chosen date is already held the backend
     rejects the whole request and nothing is written, so nobody ends up owning
     half a selection. The 409 names the firm already holding it — showing that
     inline is the entire point. */
  const handlePlaceHolds = async () => {
    if (savingHold || holdDates.length === 0) return;
    /* The same gate the Review button applies, enforced where the write
       actually happens. A selection with no slot falls through portionSlots()
       to Full Day and resolveHoldDuration() to a null pair, so an incomplete
       entry does not fail — it silently books the whole day forever. This is
       the last place that can refuse it. */
    if (!holdDates.every(isSelectionComplete)) {
      setHoldError('Choose a booking slot and a duration for every date first.');
      return;
    }
    setSavingHold(true);
    setHoldError('');
    try {
      /* Per date: its own slots and its own duration pair. The duration goes up
         as amount + unit, never as a phrase — the server writes the phrase and
         derives hold_expires_at from the pair, so the follow-up date can only
         come from its clock. The batch pair is still sent as a floor so a
         malformed entry cannot produce a hold with no expiry at all. */
      const selections = holdDates.map((s) => {
        const d = resolveHoldDuration(s);
        return {
          date: s.date,
          slots: portionSlots(s.portion),
          ...(d.amount != null && d.unit
            ? { duration_amount: d.amount, duration_unit: d.unit }
            : {}),
        };
      });
      const { amount, unit } = resolveHoldDuration(holdDates[0] || {});
      // Not read straight off component state: the notification preferences go
      // through the client-side gates, so one place decides what may reach the
      // wire and turning either email off is a one-line change.
      const notify = resolveHoldNotifications(holdOptions);
      const body = {
        selections,
        hold_type: holdType,
        notify_on_confirmation: notify.confirmation,
        notify_on_expiration: notify.expiration,
      };
      if (amount != null && unit) {
        body.duration_amount = amount;
        body.duration_unit = unit;
      }
      const res = await api.post(`/inquiries/${id}/holds`, body);
      const n = holdDates.length;
      setShowHoldModal(false);
      setHoldDates([]);
      setActiveHoldDate('');
      setHoldScreen('config');
      setHoldType('soft');
      setHoldOptions(HOLD_COMPOSER_DEFAULTS);
      fetchInquiry();
      refreshHoldCalendar();
      /* Say whether the firm was actually told rather than leaving it to be
         assumed. A skip is a normal answer (the mediator may not be opted in to
         hold emails, or the inquiry may have no contact address) and saying
         nothing about it is what makes someone follow up by hand anyway. */
      const sentTo = res.data?.data?.confirmation_email?.sent
        ? res.data?.data?.confirmation_email?.email
        : null;
      setToast(`${n} date${n === 1 ? '' : 's'} held${sentTo ? ` — ${sentTo} notified` : ''}`);
    } catch (err) {
      console.error('Failed to hold dates:', err);
      // Clashes and volume caps come back as `message`; the validation 400s on
      // the duration and notification fields come back as `error`. Reading only
      // one of the two shows a generic failure for a problem the server named
      // precisely.
      setHoldError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Could not hold those dates'
      );
    } finally {
      setSavingHold(false);
    }
  };

  /* A hold hardens over time — pencilled in, then chased, then verbally agreed.
     Changing the type in place keeps the hold's original placement stamp, and
     therefore its ELAPSED count; releasing and re-placing it would lose both. */
  const handleChangeHoldType = async (date, slot, nextType) => {
    const prev = inquiry?.holds || [];
    // Optimistic: the menu should not sit on the old value while the request is
    // in flight.
    setInquiry((cur) =>
      cur
        ? {
            ...cur,
            holds: (cur.holds || []).map((h) => {
              if (h.date !== date) return h;
              const slots = (h.slots || []).map((sl) =>
                !slot || sl.slot === slot ? { ...sl, hold_type: nextType } : sl
              );
              // Re-derive the date-level type the way the server does: a value
              // only when every held half agrees, null otherwise. Writing
              // nextType here regardless would make retyping one half of a split
              // day collapse its two cards into a single "Full day" one until
              // the page was refetched.
              const agreed =
                slots.length > 0 && slots.every((sl) => sl.hold_type === slots[0].hold_type);
              return { ...h, hold_type: agreed ? slots[0].hold_type : null, slots };
            }),
          }
        : cur
    );
    try {
      await api.patch(`/inquiries/${id}/holds/${date}${slot ? `/${slot}` : ''}`, {
        hold_type: nextType,
      });
      setToast('Hold updated');
    } catch (err) {
      console.error('Failed to change hold type:', err);
      setInquiry((cur) => (cur ? { ...cur, holds: prev } : cur));
      setToast('Could not change the hold type');
    }
  };

  /* Release asks before it acts, because the interesting half of the decision
     is not "release?" but "does the firm get told?". They were promised these
     dates; letting them go silently is a choice, and so is emailing them. */
  const handleReleaseHold = (date, slot) => {
    if (releasingDate) return;
    setPendingRelease({ date, slot });
  };

  const confirmReleaseHold = async (notify) => {
    const target = pendingRelease;
    if (!target || releasingDate) return;
    setPendingRelease(null);
    setReleasingDate(holdKey(target.date, target.slot));
    try {
      const res = await api.delete(
        `/inquiries/${id}/holds/${target.date}${target.slot ? `/${target.slot}` : ''}${
          notify ? '?notify=true' : ''
        }`
      );
      fetchInquiry();
      refreshHoldCalendar();
      // Same honesty as the hold toast: name the address when a send actually
      // happened, so a skip is visible rather than assumed.
      const sentTo = res.data?.data?.release_email?.sent
        ? res.data?.data?.release_email?.email
        : null;
      setToast(`Date released${sentTo ? ` — ${sentTo} notified` : ''}`);
    } catch (err) {
      console.error('Failed to release hold:', err);
      setToast(err?.response?.data?.message || 'Could not release that date');
    } finally {
      setReleasingDate('');
    }
  };

  // Built once per render so the "Held dates" section, its count and the modal
  // list all read the same cards.
  const holdCards = buildHoldCards(inquiry?.holds || []);

  const linkedLabel = (() => {
    if (inquiry?.convertedCaseId) {
      const c = casesList.find((x) => String(x.id) === String(inquiry.convertedCaseId));
      return c?.name || `Case #${inquiry.convertedCaseId}`;
    }
    if (inquiry?.status === 'Converted') {
      const linkComment = comments.find((c) => c.text?.startsWith('Linked to Case'));
      if (linkComment?.text) {
        const m = linkComment.text.match(/Linked to Case #?(\S+)(?:\s*—\s*(.*))?/);
        if (m) return m[2]?.trim() || `Case #${m[1]}`;
      }
    }
    return null;
  })();
  const isLinked = Boolean(linkedLabel);
  const openTaskCount = tasks.filter((t) => !t.completed).length;

  const handleViewLinkedCase = () => {
    if (inquiry?.convertedCaseId) navigate(`/cases/${inquiry.convertedCaseId}`);
  };

  return (
    <div className="flex flex-col relative" style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {loading && (
        <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
          </div>
        </div>
      )}
      {/* Header Bar - 60px */}
      <div
        className="flex items-center justify-between shrink-0"
        style={{ height: 60, padding: '16px 28px', borderBottom: '1px solid #e5e7eb' }}
      >
        {/* Header Left */}
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate('/inquiries')}
        >
          <BackArrowIcon />
          <span
            style={{
              fontFamily: '"Inter Tight", sans-serif',
              fontSize: 15,
              fontWeight: 600,
              color: '#111827',
              lineHeight: 1.5,
            }}
          >
            Inquiries / {inquiry?.inquiryNumber || id}
          </span>
        </div>

        {/* Header Right */}
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center rounded-full"
            style={{
              padding: '4px 12px',
              backgroundColor: '#dcfce7',
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              color: '#166534',
            }}
          >
            {inquiry?.status || 'Loading...'}
          </span>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto" style={{ padding: '24px 28px 40px 28px' }}>
        {/* ===== Summary Hero Card (redesigned) =====
            Single intentional card with: status-tinted left accent rail,
            primary identity block, scannable meta strip, and a right-side
            action stack. Replaces the previous abandoned-columns layout. */}
        {(() => {
          const raw = inquiry?.status || 'In Progress';
          const status = raw === 'New' ? 'In Progress' : raw;
          const sStyle = getStatusStyle(status);
          // Split "Firm - Name" back into separate strings for hierarchy.
          const rpRaw = inquiry?.requestingParty || '';
          const dashIdx = rpRaw.indexOf(' - ');
          const firmText = dashIdx > 0 ? rpRaw.slice(0, dashIdx).trim() : '';
          const partyName = dashIdx > 0 ? rpRaw.slice(dashIdx + 3).trim() : rpRaw;
          return (
            <div
              // Note: `overflow-hidden` removed.  The status dropdown panel
              // anchors below the card's right edge; with the card's
              // overflow:hidden it was being clipped to a sliver.  No
              // children exceed the rounded corners, so dropping it has no
              // other visual effect.
              className="relative"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
                marginBottom: 20,
              }}
            >
              <div className="flex" style={{ padding: '20px 24px 4px 24px', gap: 24 }}>
                {/* LEFT: identity + contact */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#6b7280',
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                      }}
                    >
                      {inquiry?.inquiryNumber || `Inquiry #${id}`}
                    </span>
                    {firmText && (
                      <>
                        <span style={{ color: '#d1d5db', fontSize: 11 }}>•</span>
                        <span
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 12,
                            fontWeight: 500,
                            color: '#6b7280',
                          }}
                          title={firmText}
                        >
                          {firmText}
                        </span>
                      </>
                    )}
                  </div>

                  <h1
                    style={{
                      fontFamily: '"Inter Tight", sans-serif',
                      fontSize: 26,
                      fontWeight: 700,
                      color: '#0e1e20',
                      lineHeight: 1.15,
                      letterSpacing: -0.3,
                      marginBottom: 12,
                      wordBreak: 'break-word',
                    }}
                  >
                    {partyName || '—'}
                  </h1>

                  {/* Contact row — email with inline copy affordance */}
                  {inquiry?.email && (
                    <div className="flex items-center flex-wrap" style={{ gap: 8, marginBottom: 14 }}>
                      <a
                        href={`mailto:${inquiry.email}`}
                        className="inline-flex items-center gap-1.5 transition-colors"
                        style={{
                          padding: '5px 10px',
                          borderRadius: 8,
                          backgroundColor: '#f0fdfa',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: '#115e59',
                          textDecoration: 'none',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#ccfbf1')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f0fdfa')}
                      >
                        <EnvelopeIcon />
                        {inquiry.email}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleCopyValue(inquiry.email, 'email')}
                        aria-label="Copy email"
                        title={copiedField === 'email' ? 'Copied' : 'Copy email'}
                        className="relative inline-flex items-center justify-center transition-colors"
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 6,
                          backgroundColor: copiedField === 'email' ? '#e8fdf5' : 'transparent',
                          border: '1px solid',
                          borderColor: copiedField === 'email' ? '#a7f3d0' : '#e5e7eb',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (copiedField !== 'email') e.currentTarget.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          if (copiedField !== 'email') e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {copiedField === 'email' ? <CheckMarkIcon /> : <CopyIcon />}
                        {copiedField === 'email' && (
                          <span
                            style={{
                              position: 'absolute',
                              top: -28,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              padding: '3px 7px',
                              backgroundColor: '#0e1e20',
                              color: '#fff',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: 10.5,
                              fontWeight: 500,
                              borderRadius: 4,
                              whiteSpace: 'nowrap',
                              pointerEvents: 'none',
                            }}
                          >
                            Copied
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Meta strip — single scannable row. All three fragments use
                      a uniform 20×20 icon slot so labels share a vertical baseline. */}
                  <div
                    className="flex items-center flex-wrap"
                    style={{
                      gap: 0,
                      paddingTop: 14,
                      borderTop: '1px solid #f1f3f5',
                    }}
                  >
                    {/* Mediator */}
                    <div className="flex items-center" style={{ gap: 10, padding: '6px 16px 6px 0' }}>
                      <div
                        className="flex items-center justify-center rounded-full shrink-0"
                        style={{ width: 20, height: 20, backgroundColor: '#115e59' }}
                      >
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 9, fontWeight: 600, color: '#fff', lineHeight: 1 }}>
                          {inquiry?.mediator ? getInitials(inquiry.mediator) : '—'}
                        </span>
                      </div>
                      <div className="flex flex-col" style={{ lineHeight: 1.25 }}>
                        <span style={metaLabelStyle}>Mediator</span>
                        <span style={metaValueStyle}>{inquiry?.mediator || '—'}</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: '#f1f3f5', margin: '8px 0' }} />

                    {/* Case Name — the inquiry's OWN case name (item 4), shown
                        pre-conversion. Distinct from any linked/converted case,
                        which is surfaced in the right-hand action stack. */}
                    <div className="flex items-center" style={{ gap: 10, padding: '6px 16px' }}>
                      <div className="flex items-center justify-center shrink-0" style={{ width: 20, height: 20 }}>
                        <UserIcon color="#6b7280" />
                      </div>
                      <div className="flex flex-col" style={{ lineHeight: 1.25 }}>
                        <span style={metaLabelStyle}>Case Name</span>
                        <span style={metaValueStyle}>{inquiry?.caseName || '—'}</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: '#f1f3f5', margin: '8px 0' }} />

                    {/* Submission Date */}
                    <div className="flex items-center" style={{ gap: 10, padding: '6px 16px' }}>
                      <div className="flex items-center justify-center shrink-0" style={{ width: 20, height: 20 }}>
                        <CalendarIcon color="#6b7280" />
                      </div>
                      <div className="flex flex-col" style={{ lineHeight: 1.25 }}>
                        <span style={metaLabelStyle}>Submitted</span>
                        <span style={metaValueStyle}>{inquiry?.submissionDateLabel || '—'}</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: '#f1f3f5', margin: '8px 0' }} />

                    {/* Follow-Up Date */}
                    <div className="flex items-center" style={{ gap: 10, padding: '6px 16px' }}>
                      <div className="flex items-center justify-center shrink-0" style={{ width: 20, height: 20 }}>
                        <CalendarIcon color="#6b7280" />
                      </div>
                      <div className="flex flex-col" style={{ lineHeight: 1.25 }}>
                        <span style={metaLabelStyle}>Follow-Up</span>
                        <span style={metaValueStyle}>{inquiry?.followUpDateLabel || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: action stack */}
                <div
                  className="flex flex-col items-stretch shrink-0"
                  style={{ gap: 10, minWidth: 200 }}
                >
                  {/* Brand-styled status select. Teal border + teal text +
                      teal chevron — same chrome regardless of which status
                      is selected. A small status-colored dot still hints
                      at the active value (New / In Progress / Converted /
                      Not Converted). */}
                  <div data-status-menu className="relative">
                    <button
                      type="button"
                      onClick={() => setStatusMenuOpen((v) => !v)}
                      disabled={savingStatus}
                      className="inline-flex items-center justify-between w-full transition-colors"
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        backgroundColor: statusMenuOpen ? '#f0fdfa' : '#ffffff',
                        border: '1px solid #115e59',
                        cursor: savingStatus ? 'wait' : 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        if (!statusMenuOpen && !savingStatus) e.currentTarget.style.backgroundColor = '#f0fdfa';
                      }}
                      onMouseLeave={(e) => {
                        if (!statusMenuOpen) e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                    >
                      <span className="inline-flex items-center" style={{ gap: 8 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: sStyle.dot,
                            display: 'inline-block',
                          }}
                        />
                        <span
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#115e59',
                            letterSpacing: 0.1,
                          }}
                        >
                          {savingStatus ? 'Saving…' : status}
                        </span>
                      </span>
                      <ChevronTinyIcon color="#115e59" />
                    </button>
                    {statusMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          right: 0,
                          left: 0,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(16,24,40,0.10)',
                          padding: 4,
                          zIndex: 20,
                        }}
                      >
                        <div
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 10.5,
                            fontWeight: 600,
                            color: '#9ca3af',
                            letterSpacing: 0.4,
                            textTransform: 'uppercase',
                            padding: '7px 10px 4px 10px',
                          }}
                        >
                          Change status
                        </div>
                        {STATUS_OPTIONS_ALL.map((opt) => {
                          const oStyle = getStatusStyle(opt);
                          const isActive = opt === status;
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => handleStatusChange(opt)}
                              className="flex items-center w-full transition-colors"
                              style={{
                                padding: '7px 10px',
                                borderRadius: 6,
                                gap: 8,
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <span
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: '50%',
                                  backgroundColor: oStyle.dot,
                                  display: 'inline-block',
                                }}
                              />
                              <span
                                style={{
                                  flex: 1,
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: 12.5,
                                  fontWeight: isActive ? 600 : 500,
                                  color: '#111827',
                                }}
                              >
                                {opt}
                              </span>
                              {isActive && <CheckMarkIcon color="#115e59" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Linked case CTA */}
                  {isLinked ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={handleViewLinkedCase}
                      disabled={!inquiry?.convertedCaseId}
                      className="inline-flex items-center justify-center gap-2 transition-colors"
                      style={{
                        padding: '9px 14px',
                        borderRadius: 8,
                        backgroundColor: '#115e59',
                        color: '#ffffff',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: inquiry?.convertedCaseId ? 'pointer' : 'default',
                        border: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (inquiry?.convertedCaseId) e.currentTarget.style.backgroundColor = '#0e4f4a';
                      }}
                      onMouseLeave={(e) => {
                        if (inquiry?.convertedCaseId) e.currentTarget.style.backgroundColor = '#115e59';
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        View {linkedLabel}
                      </span>
                      <ExternalLinkIcon color="#ffffff" />
                    </button>
                    <button
                      type="button"
                      onClick={handleUnlinkCase}
                      disabled={linkingCase}
                      className="inline-flex items-center justify-center transition-colors"
                      style={{
                        padding: '9px 14px',
                        borderRadius: 8,
                        backgroundColor: '#ffffff',
                        color: '#b91c1c',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: linkingCase ? 'default' : 'pointer',
                        border: '1px solid #fecaca',
                      }}
                    >
                      Unlink
                    </button>
                    </div>
                  ) : (
                    <div
                      className="flex items-center justify-center"
                      style={{
                        padding: '9px 12px',
                        borderRadius: 8,
                        backgroundColor: '#f9fafb',
                        border: '1px dashed #e5e7eb',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11.5,
                        fontWeight: 500,
                        color: '#9ca3af',
                        textAlign: 'center',
                      }}
                    >
                      Not yet linked to a case
                    </div>
                  )}

                  {/* Pencil dates in for this firm before anyone commits.
                      Amber because that is the colour the schedule already
                      paints a held day. */}
                  <button
                    type="button"
                    onClick={() => setShowHoldModal(true)}
                    className="inline-flex items-center justify-center gap-2 transition-colors"
                    style={{
                      padding: '9px 12px',
                      borderRadius: 8,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e3c489',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: '#B45309',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fbf1dd')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                  >
                    <HoldCalendarIcon />
                    {inquiry?.holds?.length ? `Hold Dates · ${inquiry.holds.length}` : 'Hold Dates'}
                  </button>

                  {/* Edit / Delete row */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(true)}
                      className="inline-flex items-center justify-center gap-2 transition-colors"
                      style={{
                        flex: 1,
                        padding: '9px 12px',
                        borderRadius: 8,
                        backgroundColor: '#ffffff',
                        color: '#115e59',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: '1px solid #115e59',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdfa')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                    >
                      Edit details
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(true)}
                      aria-label="Delete inquiry"
                      title="Delete inquiry"
                      className="inline-flex items-center justify-center transition-colors"
                      style={{
                        padding: '9px 12px',
                        borderRadius: 8,
                        backgroundColor: '#ffffff',
                        color: '#b91c1c',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: '1px solid #fecaca',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Notes — preserved as an in-card section, conditional */}
              {inquiry?.notes && (
                <div
                  style={{
                    margin: '12px 24px 20px 24px',
                    padding: '12px 14px',
                    borderRadius: 8,
                    backgroundColor: '#f9fafb',
                    border: '1px solid #f1f3f5',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: '#6b7280',
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Notes
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 13.5,
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {inquiry.notes}
                  </div>
                </div>
              )}

              {/* Toast for status updates */}
              {toast && (
                <div
                  role="status"
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    right: 16,
                    padding: '7px 12px',
                    backgroundColor: '#0e1e20',
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 12,
                    fontWeight: 500,
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(16,24,40,0.18)',
                  }}
                >
                  {toast}
                </div>
              )}
            </div>
          );
        })()}

        {/* Follow-up Tasks */}
        <div style={{ padding: '24px 24px 0 24px' }}>
          {/* Tasks Header */}
          <div className="flex items-center justify-between" style={{ marginBottom: 0 }}>
            <div className="flex items-baseline gap-2">
              <h3
                style={{
                  fontFamily: '"Inter Tight", sans-serif',
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#0e1e20',
                  lineHeight: 1.5,
                }}
              >
                Follow-up Tasks
              </h3>
              {tasks.length > 0 && (
                <span
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#71717a',
                  }}
                >
                  {openTaskCount > 0 ? `${openTaskCount} open · ${tasks.length} total` : `${tasks.length} total`}
                </span>
              )}
            </div>
            <button
              className="flex items-center gap-[6px] rounded-lg"
              style={{
                backgroundColor: '#115e59',
                padding: '6px 12px',
              }}
              onClick={() => setShowAddTask(!showAddTask)}
            >
              <PlusIcon />
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#ffffff',
                }}
              >
                Add Task
              </span>
            </button>
          </div>

          {/* Task Table Header */}
          <div
            className="flex items-center"
            style={{
              height: 36,
              padding: '0 12px',
              borderBottom: '1px solid #e5e7eb',
              marginTop: 0,
            }}
          >
            <div style={{ width: 30 }} />
            <span
              className="flex-1"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                color: '#71717a',
              }}
            >
              Task
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                color: '#71717a',
                width: 100,
              }}
            >
              Due Date
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                color: '#71717a',
                width: 150,
              }}
            >
              Assignee
            </span>
          </div>

          {/* Task Rows */}
          {tasks.map((t) => (
            <div
              key={t.id}
              className="flex items-center"
              style={{
                height: 44,
                padding: '0 12px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              {/* Checkbox */}
              {t.completed ? (
                <div
                  className="flex items-center justify-center rounded cursor-pointer"
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: '#115e59',
                    borderRadius: 4,
                  }}
                  onClick={() => handleToggleTask(t.id)}
                >
                  <CheckIcon />
                </div>
              ) : (
                <div
                  className="cursor-pointer"
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: '1px solid #d4d4d8',
                  }}
                  onClick={() => handleToggleTask(t.id)}
                />
              )}

              {/* Spacer */}
              <div style={{ width: 12 }} />

              {/* Task text */}
              <span
                className="flex-1"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#18181b',
                }}
              >
                {t.task}
              </span>

              {/* Due date */}
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#71717a',
                  width: 100,
                }}
              >
                {t.dueDate}
              </span>

              {/* Assignee */}
              <div className="flex items-center gap-2" style={{ width: 150 }}>
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 24,
                    height: 24,
                    backgroundColor: t.avatarBg || '#115e59',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#ffffff',
                    }}
                  >
                    {t.assignee}
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 13,
                    fontWeight: 400,
                    color: '#18181b',
                  }}
                >
                  {t.assignee}
                </span>
              </div>
            </div>
          ))}

          {/* Add Task Popup */}
          {showAddTask && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} onClick={() => { setShowAddTask(false); setNewTask({ task: '', dueDate: '', assignee: '' }); }}>
              <div
                className="bg-white rounded-2xl flex flex-col"
                style={{ width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', border: '1px solid #e5e7eb' }}
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-6 pb-4">
                  <h3 style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 18, fontWeight: 700, color: '#111827' }}>Add Task</h3>
                  <button onClick={() => { setShowAddTask(false); setNewTask({ task: '', dueDate: '', assignee: '' }); }} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" /></svg>
                  </button>
                </div>

                {/* Form */}
                <div className="px-6 pb-2 flex flex-col gap-4">
                  {/* Task Name */}
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: '#374151' }}>Task Name</label>
                    <input
                      type="text"
                      placeholder="Enter task name..."
                      value={newTask.task}
                      onChange={e => setNewTask(t => ({ ...t, task: e.target.value }))}
                      className="h-10 px-3 rounded-lg border border-[#e5e7eb] text-sm outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                      style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#111827' }}
                    />
                  </div>

                  {/* Due Date */}
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: '#374151' }}>Due Date</label>
                    <input
                      type="date"
                      value={newTask.dueDate}
                      onChange={e => setNewTask(t => ({ ...t, dueDate: e.target.value }))}
                      className="h-10 px-3 rounded-lg border border-[#e5e7eb] text-sm outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                      style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: newTask.dueDate ? '#111827' : '#9ca3af' }}
                    />
                  </div>

                  {/* Assignee */}
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: '#374151' }}>Assignee</label>
                    <div className="flex items-center gap-2.5 h-10 px-3 rounded-lg border border-[#e5e7eb]">
                      <div className="w-6 h-6 rounded-full bg-[#115e59] flex items-center justify-center flex-shrink-0">
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 9, fontWeight: 600, color: '#fff' }}>
                          {(newTask.assignee || user?.firstName?.[0] || 'U').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder={user?.firstName ? `${user.firstName} ${user.lastName || ''}` : 'Assignee name'}
                        value={newTask.assignee}
                        onChange={e => setNewTask(t => ({ ...t, assignee: e.target.value }))}
                        className="flex-1 h-full border-none outline-none text-sm bg-transparent"
                        style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#111827' }}
                      />
                      <ChevronDownIcon />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-5">
                  <button
                    onClick={() => { setShowAddTask(false); setNewTask({ task: '', dueDate: '', assignee: '' }); }}
                    className="h-9 px-5 rounded-lg border border-[#e5e7eb] hover:bg-gray-50"
                    style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500, color: '#4b5563' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTask}
                    disabled={addingTask || !newTask.task.trim()}
                    className="h-9 px-5 rounded-lg flex items-center gap-1.5"
                    style={{ backgroundColor: addingTask || !newTask.task.trim() ? '#94a3b8' : '#115e59', fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' }}
                  >
                    <PlusIcon />
                    {addingTask ? 'Adding...' : 'Add Task'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div
          style={{
            padding: 24,
            borderTop: '1px solid #e5e5e5',
            marginTop: 24,
          }}
        >
          <div className="flex items-baseline gap-2" style={{ marginBottom: 20 }}>
            <h3
              style={{
                fontFamily: '"Inter Tight", sans-serif',
                fontSize: 15,
                fontWeight: 700,
                color: '#0e1e20',
                lineHeight: 1.5,
              }}
            >
              Comments
            </h3>
            {comments.length > 0 && (
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#71717a',
                }}
              >
                {comments.length}
              </span>
            )}
          </div>

          {/* Comments List */}
          {comments.length === 0 && !loading && (
            <div className="flex items-center justify-center" style={{ height: 60, marginBottom: 20 }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#71717a' }}>No comments yet</span>
            </div>
          )}
          {comments.map((comment, idx) => (
            <React.Fragment key={comment.id || idx}>
              <div
                className="flex gap-3 rounded-xl"
                style={{
                  backgroundColor: '#f9fafb',
                  padding: '12px 14px',
                  marginBottom: 20,
                }}
              >
                <div
                  className="flex items-center justify-center rounded-full shrink-0"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#115e59',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#ffffff',
                    }}
                  >
                    {comment.initials}
                  </span>
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#18181b',
                        lineHeight: 1.43,
                      }}
                    >
                      {comment.name}
                    </span>
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11,
                        fontWeight: 400,
                        color: '#71717a',
                      }}
                    >
                      {comment.time}
                    </span>
                  </div>
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 13,
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {comment.text}
                  </p>
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* Comment Input */}
          <div className="flex flex-col items-end gap-3">
            <div
              className="rounded-xl w-full"
              style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #d4d4d8',
                padding: '12px 14px',
                height: 80,
              }}
            >
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment..."
                className="w-full h-full bg-transparent border-none outline-none resize-none"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#18181b',
                }}
              />
            </div>
            <button
              className="flex items-center justify-center rounded-lg"
              style={{
                backgroundColor: '#115e59',
                height: 36,
                padding: '0 16px',
                opacity: submittingComment || !commentText.trim() ? 0.6 : 1,
              }}
              onClick={handlePostComment}
              disabled={submittingComment || !commentText.trim()}
            >
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#ffffff',
                }}
              >
                {submittingComment ? 'Posting...' : 'Post Comment'}
              </span>
            </button>
          </div>
        </div>

        {/* Held dates — the same cards the hold modal shows, on the page itself.

            They were only ever visible inside the modal, which meant the one
            question this page gets opened to answer — what are we holding, and
            for how long — required opening a dialog built for PLACING holds.
            Same card, same controls: retype a hold or release it without going
            through the composer.

            Hidden entirely when nothing is held rather than showing an empty
            state: an inquiry with no holds has nothing to say here, and a
            permanent empty panel is noise on every other inquiry. */}
        {holdCards.length > 0 && (
          <div style={{ padding: 24, borderTop: '1px solid #e5e5e5' }}>
            <div className="flex items-baseline gap-2" style={{ marginBottom: 20 }}>
              <h3
                style={{
                  fontFamily: '"Inter Tight", sans-serif',
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#0e1e20',
                  lineHeight: 1.5,
                }}
              >
                Held Dates
              </h3>
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#71717a',
                }}
              >
                {holdCards.length}
              </span>
            </div>
            <div className="flex flex-col" style={{ gap: 10 }}>
              {holdCards.map((c) => (
                <HoldCard
                  key={c.key}
                  card={c}
                  onChangeType={(v) => handleChangeHoldType(c.date, c.slot, v)}
                  onRelease={() => handleReleaseHold(c.date, c.slot)}
                  releasing={releasingDate === holdKey(c.date, c.slot)}
                  releaseDisabled={!!releasingDate}
                />
              ))}
            </div>
          </div>
        )}

        {/* Link to Case - hidden when already linked (linked case is in the summary strip) */}
        {!isLinked && (
        <div
          style={{
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e5e5e5',
            padding: '24px',
            marginTop: 0,
          }}
        >
          <h3
            style={{
              fontFamily: '"Inter Tight", sans-serif',
              fontSize: 15,
              fontWeight: 700,
              color: '#0e1e20',
              lineHeight: 1.5,
              marginBottom: 8,
            }}
          >
            Link to Case
          </h3>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              color: '#6b7280',
              marginBottom: 16,
            }}
          >
            Convert this inquiry by linking it to an existing case.
          </p>

          {/* Link Row */}
          <div className="flex items-center gap-[10px]" style={{ marginBottom: 0 }}>
            {/* Searchable case dropdown */}
            <div data-case-dropdown style={{ position: 'relative', width: 360 }}>
              <button
                type="button"
                onClick={() => setCaseDropdownOpen((v) => !v)}
                className="rounded-lg w-full flex items-center justify-between"
                style={{
                  height: 40,
                  padding: '0 14px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  fontWeight: 400,
                  color: selectedCaseId ? '#111827' : '#9ca3af',
                  outline: 'none',
                }}
              >
                <span className="truncate">
                  {(() => {
                    const c = casesList.find(x => String(x.id) === String(selectedCaseId));
                    return c ? `${c.name} (#${c.id})` : 'Search and select a case...';
                  })()}
                </span>
                <ChevronDownIcon />
              </button>
              {caseDropdownOpen && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-lg z-50 overflow-hidden"
                  style={{ maxHeight: 320 }}
                >
                  {/* Search input */}
                  <div className="flex items-center gap-2 px-3" style={{ borderBottom: '1px solid #e5e7eb', height: 38 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="6.417" cy="6.417" r="4.667" stroke="#9ca3af" strokeWidth="1.167" />
                      <path d="M9.742 9.742l2.508 2.508" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" />
                    </svg>
                    <input
                      type="text"
                      value={caseSearch}
                      onChange={(e) => setCaseSearch(e.target.value)}
                      placeholder="Search cases by name or ID..."
                      autoFocus
                      className="flex-1 bg-transparent border-0 outline-none text-[13px]"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                  {/* Filtered options */}
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {(() => {
                      const q = caseSearch.trim().toLowerCase();
                      const filtered = !q ? casesList : casesList.filter(c =>
                        (c.name || '').toLowerCase().includes(q) ||
                        String(c.id).includes(q)
                      );
                      if (filtered.length === 0) {
                        return (
                          <div className="px-3 py-3 text-[13px] text-[#9ca3af]" style={{ fontFamily: 'Inter, sans-serif' }}>
                            No matching cases
                          </div>
                        );
                      }
                      return filtered.map(c => {
                        const active = String(c.id) === String(selectedCaseId);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => { setSelectedCaseId(c.id); setCaseDropdownOpen(false); setCaseSearch(''); }}
                            className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${active ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#0e1e20]'}`}
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {c.name} <span className="text-[#9ca3af]">(#{c.id})</span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Link Button */}
            <button
              onClick={handleLinkCase}
              disabled={!selectedCaseId || linkingCase}
              className="flex items-center justify-center rounded-lg"
              style={{
                backgroundColor: !selectedCaseId || linkingCase ? '#94a3b8' : '#115e59',
                height: 40,
                padding: '0 20px',
                cursor: !selectedCaseId ? 'not-allowed' : 'pointer',
              }}
            >
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#ffffff' }}>
                {linkingCase ? 'Linking...' : 'Link'}
              </span>
            </button>
          </div>

        </div>
        )}
      </div>

      {/* Edit details modal */}
      {showEditModal && inquiry && (
        <EditInquiryModal
          id={id}
          initial={{
            requestingParty: inquiry.requestingPartyName,
            email: inquiry.email,
            phone: inquiry.phone,
            firm: inquiry.firm,
            caseName: inquiry.caseName,
            caseType: inquiry.caseType,
            submissionDate: inquiry.submissionDate,
            followUpDate: inquiry.followUpDate,
          }}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { fetchInquiry(); setToast('Inquiry updated'); }}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <DeleteInquiryModal
          inquiryNumber={inquiry?.inquiryNumber}
          deleting={deleting}
          onCancel={() => { if (!deleting) setShowDeleteModal(false); }}
          onConfirm={handleDeleteInquiry}
        />
      )}

      {/* Release confirmation. Two ways to say yes, because the decision is
          about the firm rather than about the date: the date goes back either
          way, and the only question is whether the firm hears about it.
          "Release & notify" is primary — they were promised these dates, so
          telling them is the considerate default — but a date released because
          they just booked it should go quietly, which is the other button. */}
      {pendingRelease && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.4)', padding: 16 }}
          onClick={() => setPendingRelease(null)}
        >
          <div
            className="flex flex-col bg-white w-full"
            style={{ maxWidth: 440, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: 'Inter, sans-serif' }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape') setPendingRelease(null); }}
          >
            <div className="flex items-start justify-between" style={{ padding: '20px 24px 16px 24px', borderBottom: '1px solid #f1f3f5' }}>
              <div className="flex flex-col" style={{ gap: 2 }}>
                <h3 style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 16, fontWeight: 700, color: '#111827' }}>
                  Release {formatShortDate(pendingRelease.date)}
                  {pendingRelease.slot ? ` (${pendingRelease.slot})` : ''}?
                </h3>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#6b7280', margin: 0 }}>
                  The date goes back on the mediator’s calendar either way.
                </p>
              </div>
              <button
                onClick={() => setPendingRelease(null)}
                aria-label="Close"
                className="flex items-center justify-center"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </div>

            <div style={{ padding: '16px 24px' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#4b5563', lineHeight: 1.6, margin: 0 }}>
                {inquiry?.email ? (
                  <>
                    Do you want to email{' '}
                    <span style={{ fontWeight: 600, color: '#111827' }}>{inquiry.email}</span>
                    {inquiry?.firm ? <> at {inquiry.firm}</> : null} to say the date has been released?
                  </>
                ) : (
                  <>This inquiry has no contact email, so nobody can be notified.</>
                )}
              </p>
            </div>

            <div className="flex items-center justify-end" style={{ padding: '16px 24px', gap: 12, borderTop: '1px solid #f1f3f5' }}>
              <button
                onClick={() => confirmReleaseHold(false)}
                style={{ height: 36, padding: '0 16px', borderRadius: 8, border: 'none', background: 'transparent', fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#4b5563', cursor: 'pointer' }}
              >
                Release without email
              </button>
              <button
                onClick={() => confirmReleaseHold(true)}
                disabled={!inquiry?.email}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', backgroundColor: inquiry?.email ? '#115e59' : '#94a3b8', fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#ffffff', cursor: inquiry?.email ? 'pointer' : 'not-allowed' }}
              >
                Release &amp; notify firm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Hold-dates modal — pencil dates in for this inquiry's firm. Saving
          keeps you here; the dates surface on the mediator's schedule as
          "On Hold – {firm}". */}
      {showHoldModal && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.4)', padding: 16 }}
          onClick={handleCloseHold}
        >
          <div
            className="flex flex-col bg-white w-full"
            // Wider than a normal CM modal: the composer carries five duration
            // pills on one row and the hold cards a two-column statistic row.
            style={{
              maxWidth: holdScreen === 'review' ? 640 : 1060,
              maxHeight: '88vh',
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              fontFamily: 'Inter, sans-serif',
              transition: 'max-width .32s cubic-bezier(.22,.61,.36,1)',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !e.defaultPrevented) handleCloseHold();
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                /* The shortcut does what the primary button does, which is NOT
                   always "save". On the config screen that button says Review,
                   and firing the save from here would skip both the review and
                   its completeness gate — writing a full-day hold nobody chose,
                   with no duration and so no expiry, and emailing the firm
                   about it. */
                if (holdScreen === 'review') handlePlaceHolds();
                else if (holdDates.length && holdDates.every(isSelectionComplete)) {
                  setHoldScreen('review');
                }
              }
            }}
          >
            <div className="flex items-start justify-between shrink-0" style={{ padding: '20px 24px 16px 24px', borderBottom: '1px solid #f1f3f5' }}>
              <div className="flex flex-col" style={{ gap: 2 }}>
                <h3 style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 16, fontWeight: 700, color: '#111827' }}>Hold Dates</h3>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#6b7280', margin: 0 }}>
                  {inquiry?.firm
                    ? `Held for ${inquiry.firm} and shown on the mediator’s schedule.`
                    : 'Held dates are shown on the mediator’s schedule.'}
                </p>
              </div>
              <button
                onClick={handleCloseHold}
                aria-label="Close"
                className="flex items-center justify-center"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </div>

            <div className="flex flex-col overflow-y-auto" style={{ padding: '20px 24px', gap: 16 }}>
              {holdError && (
                <div className="flex items-center" style={{ gap: 8, borderRadius: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="6.5" stroke="#dc2626" strokeWidth="1.3" /><path d="M8 5v3.5M8 10.5v.01" stroke="#dc2626" strokeWidth="1.3" strokeLinecap="round" /></svg>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#dc2626' }}>{holdError}</span>
                </div>
              )}

              {holdScreen === 'review' ? (
                /* Review before saving. One row per date with its own slot and
                   duration, each editable — the whole point of holding several
                   dates is that they can differ. */
                <div className="flex flex-col" style={{ gap: 8 }}>
                  {/* The last screen before committing, and the only one that
                      summarises the whole batch — so it has to state what will
                      actually happen. Derived from the same resolver the save
                      uses, so the two cannot drift. */}
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#6b7280', margin: '0 0 4px' }}>
                    {holdDates.length} date{holdDates.length === 1 ? '' : 's'} will be held
                    {inquiry?.firm ? ` for ${inquiry.firm}` : ''}.{' '}
                    {resolveHoldNotifications(holdOptions).confirmation
                      ? `A confirmation email goes to ${inquiry?.email || 'the inquiry contact'} on save.`
                      : 'No email is sent on save.'}
                  </p>
                  {holdDates.map((s) => {
                    const d = resolveHoldDuration(s);
                    const slot = PORTION_OPTIONS.find((p) => p.key === s.portion);
                    return (
                      <div
                        key={s.date}
                        className="flex items-center"
                        style={{ gap: 12, borderRadius: 12, padding: '12px 14px', border: '1px solid #E7E5DF', background: '#fff' }}
                      >
                        <span style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 15, fontWeight: 700, width: 84, flexShrink: 0 }}>
                          {formatShortDate(s.date)}
                        </span>
                        <span className="flex flex-wrap min-w-0" style={{ flex: 1, gap: 6 }}>
                          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '4px 10px', background: '#FBF1DD', color: '#4A3B16', border: '1px solid #E3C489' }}>
                            {slot?.label || '—'}
                          </span>
                          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '4px 10px', background: '#EAF1EC', color: '#1F3D2F' }}>
                            {d.amount ? `${d.amount} ${d.unit}` : '—'}
                          </span>
                        </span>
                        <button
                          type="button"
                          style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, fontWeight: 600, color: '#14532D', border: 'none', background: 'transparent', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}
                          onClick={() => { setActiveHoldDate(s.date); setHoldScreen('config'); }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, fontWeight: 600, color: '#B91C1C', border: 'none', background: 'transparent', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}
                          onClick={() => removeHoldDate(s.date)}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                  <div className="flex items-baseline" style={{ gap: 8, paddingTop: 12, marginTop: 4, borderTop: '1px solid #E7E5DF', fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#4B4B45' }}>
                    <b style={{ fontWeight: 600, color: '#1A1A17' }}>
                      {HOLD_TYPE_OPTIONS.find((o) => o.value === holdType)?.label || holdType}
                    </b>
                    <span>· applies to every date</span>
                    {/* The other switch, in the same summary. What happens at
                        the end of each hold is as much a property of this batch
                        as its type. */}
                    <span style={{ marginLeft: 'auto' }}>
                      {resolveHoldNotifications(holdOptions).expiration
                        ? 'Each date auto-releases when its time is up, and the firm is told'
                        : 'Dates stay held until you release them'}
                    </span>
                  </div>
                </div>
              ) : (
                /* Calendar LEFT, the active date's settings RIGHT. Stacked, this
                   runs ~1050px tall in a ~700px viewport, so it always scrolls
                   and the calendar scrolls out of view exactly when it is needed
                   as reference. */
                <div style={{ display: 'grid', gap: 40, alignItems: 'stretch', gridTemplateColumns: 'minmax(0,470px) minmax(0,1fr)' }}>
                  <div className="flex flex-col" style={{ gap: 16 }}>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#A3A39B' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 17, height: 17, borderRadius: '50%', background: '#14532D', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 0, marginRight: 9 }}>1</span>
                      Pick the dates
                    </span>
                    <HoldDatePicker
                      value={holdDates}
                      activeIso={activeHoldDate}
                      onPick={pickHoldDate}
                      dayMeta={holdCalendar}
                      onMonthChange={loadHoldCalendar}
                      loading={loadingHoldCalendar}
                    />
                  </div>

                  <div className="flex flex-col" style={{ minHeight: '100%' }}>
                    {activeSelection ? (
                      <>
                        <div className="flex items-start" style={{ gap: 12, marginBottom: 16 }}>
                          <div className="flex flex-wrap min-w-0" style={{ flex: 1, gap: 6 }}>
                            {holdDates.map((s) => {
                              const on = s.date === activeHoldDate;
                              const done = isSelectionComplete(s);
                              return (
                                <span
                                  key={s.date}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setActiveHoldDate(s.date)}
                                  onKeyDown={(e) => e.key === 'Enter' && setActiveHoldDate(s.date)}
                                  className="inline-flex items-center"
                                  style={{
                                    gap: 6,
                                    borderRadius: 999,
                                    padding: '4px 10px',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    ...(on
                                      ? { background: '#14532D', border: '1px solid #14532D', color: '#fff' }
                                      : done
                                      ? { background: '#F2F6F3', border: '1px solid #D5E4DA', color: '#1F3D2F' }
                                      : { background: '#fff', border: '1px solid #E2E1DC', color: '#4B4B45' }),
                                  }}
                                >
                                  {done && !on ? '✓ ' : ''}
                                  {formatShortDate(s.date)}
                                  <button
                                    type="button"
                                    aria-label={`Remove ${s.date}`}
                                    onClick={(e) => { e.stopPropagation(); removeHoldDate(s.date); }}
                                    style={{ background: 'none', border: 0, color: 'currentColor', opacity: 0.62, cursor: 'pointer', padding: 0 }}
                                  >
                                    ✕
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', paddingTop: 6, color: '#9ca3af' }}>
                            {holdDates.findIndex((s) => s.date === activeHoldDate) + 1} of {holdDates.length}
                          </span>
                        </div>

                        <HoldComposer
                          selection={activeSelection}
                          dayMeta={holdCalendar}
                          onChange={patchActiveSelection}
                          batch={holdOptions}
                          onBatchChange={(p) => setHoldOptions((o) => ({ ...o, ...p }))}
                          holdType={holdType}
                          onHoldTypeChange={setHoldType}
                          holdTypeOptions={HOLD_TYPE_OPTIONS}
                          inquiryContact={inquiry?.email || ''}
                          batchReady={holdDates.length > 0 && holdDates.every(isSelectionComplete)}
                          disabled={savingHold}
                        />
                      </>
                    ) : (
                      /* Centred against the calendar's height, so the column
                         reads as deliberately waiting rather than unfinished. */
                      <div className="flex flex-col items-center justify-center text-center" style={{ flex: 1, gap: 12, padding: '0 24px', minHeight: 360 }}>
                        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#CBCAC3" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="3" y="5" width="18" height="16" rx="2.5" />
                          <path d="M3 10h18M8 3v4M16 3v4" />
                        </svg>
                        {/* "Pick a date" is an instruction, and giving someone
                            an instruction they cannot yet act on is worse than
                            saying nothing. While the month is loading this says
                            what is actually happening. */}
                        <h4 style={{ margin: 0, fontFamily: '"Inter Tight", sans-serif', fontSize: 17.5, fontWeight: 600, color: '#111827' }}>
                          {loadingHoldCalendar ? 'Checking the calendar…' : 'Pick a date to start'}
                        </h4>
                        <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: 13.5, color: '#6b7280', maxWidth: 290, lineHeight: 1.6 }}>
                          {loadingHoldCalendar
                            ? 'Loading which days are free to hold.'
                            : 'Choose a day on the left to set its booking slot and duration.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Already held, with a way to retype or let each one go. One card
                  per date — except a day whose halves disagree, which splits so
                  a Confirmed morning and a Soft afternoon are never collapsed
                  into one arbitrary value. */}
              {holdCards.length > 0 && (
                <div className="flex flex-col" style={{ gap: 6 }}>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#A3A39B' }}>
                    Currently held
                  </span>
                  <div className="flex flex-col" style={{ gap: 10 }}>
                    {holdCards.map((c) => (
                      <HoldCard
                        key={c.key}
                        card={c}
                        onChangeType={(v) => handleChangeHoldType(c.date, c.slot, v)}
                        onRelease={() => handleReleaseHold(c.date, c.slot)}
                        releasing={releasingDate === holdKey(c.date, c.slot)}
                        releaseDisabled={!!releasingDate}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between shrink-0" style={{ padding: '16px 24px', gap: 12, borderTop: '1px solid #f1f3f5' }}>
              <span className="flex items-center" style={{ gap: 6, fontFamily: 'Inter, sans-serif', fontSize: 11.5, color: '#9ca3af' }}>
                <KbdKeys keys={[CMD_SYMBOL, '↵']} /> to hold
              </span>
              {/* Two steps, so two footers. Nothing saves from the configure
                  screen — the primary button there only opens the review, and it
                  stays disabled while any date is still unset so a
                  half-configured hold cannot be sent. */}
              <div className="flex items-center" style={{ gap: 12 }}>
                <button
                  onClick={holdScreen === 'review' ? () => setHoldScreen('config') : handleCloseHold}
                  style={{ height: 36, padding: '0 16px', borderRadius: 8, border: 'none', background: 'transparent', fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#4b5563', cursor: 'pointer' }}
                >
                  {holdScreen === 'review' ? 'Back' : 'Cancel'}
                </button>
                {holdScreen === 'review' ? (
                  <button
                    onClick={handlePlaceHolds}
                    disabled={holdDates.length === 0 || savingHold}
                    className="inline-flex items-center justify-center"
                    style={{
                      gap: 6, height: 36, padding: '0 20px', borderRadius: 8, border: 'none',
                      backgroundColor: holdDates.length === 0 || savingHold ? '#94a3b8' : '#115e59',
                      fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#ffffff',
                      cursor: holdDates.length === 0 || savingHold ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {savingHold && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {savingHold
                      ? 'Holding…'
                      : `Hold ${holdDates.length} date${holdDates.length === 1 ? '' : 's'}`}
                  </button>
                ) : (
                  (() => {
                    const unset = holdDates.filter((s) => !isSelectionComplete(s)).length;
                    const blocked = holdDates.length === 0 || unset > 0 || savingHold;
                    return (
                      <button
                        onClick={() => setHoldScreen('review')}
                        disabled={blocked}
                        className="inline-flex items-center justify-center"
                        style={{
                          gap: 6, height: 36, padding: '0 20px', borderRadius: 8, border: 'none',
                          backgroundColor: blocked ? '#94a3b8' : '#115e59',
                          fontFamily: 'Inter, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#ffffff',
                          cursor: blocked ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {holdDates.length === 0
                          ? 'Review'
                          : unset === 0
                          ? `Review ${holdDates.length} date${holdDates.length === 1 ? '' : 's'}`
                          : `${unset} date${unset === 1 ? '' : 's'} still to set`}
                      </button>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
